import { find } from "lodash";
import { getLocalizedOrDefault } from "../../../helpers/Functions";
import { getImageUrl } from "../../../helpers/PB";
import { localizeProductFeatures } from "../../../helpers/ScannersBijFunctions";

const ProductConfig = {
  collection: "products",
  fields: [
    "id",
    "name",

    // Intentionally collecting "brand" from "families" collection to remove mapping dependence ("2nd layer")
    "brand_family.brand.id",
    "brand_family.id",
    "model",
    "variant",
    "product_type.id",

    // Prefix with "locale."
    "locale.language",
    "locale.languages_code.code",
    "locale.linked_comparison.id",
    "locale.product_image.id",
    "locale.product_image.title",

    "locale.product_info_link_url",
    "locale.product_info_link_label",
    "locale.spec_sheet_url",
    "locale.description",

    "locale.description_disclaimer.id",

    "locale.tco_black",
    "locale.tco_color",

    "locale.short_description",

    "locale.attributes.attribute.id",
    "locale.attributes.facet",
    "locale.attributes.display_value",
    "locale.attributes.currency_value",
    "locale.attributes.choices",
    "locale.attributes.choice",
    "locale.attributes.attribute_value",
    "locale.attributes.attribute_numeric_value",
    "locale.attributes.attribute_unit",
    "locale.attributes.specification",
    "locale.attributes.facet",
    "locale.attributes.display_value",
    "locale.attributes.currency_value",
    "locale.attributes.choices",
    "locale.attributes.choice",
    "locale.attributes.disclaimer.id",

    "locale.prices.amount",
    "locale.prices.type.id",
    "locale.prices.currency_display",
    //for bij energy
    "modified_on",
    "created_on"
  ],

  filter: "filter[status][_eq]=active",

  adapter: class Product {
    constructor(product) {
      this.id = product.id;
      this.date_updated = product.modified_on || product.created_on;

      this.name = product.name || "";
      this.family = product.brand_family?.id;
      this.brand = product.brand_family?.brand?.id;
      this.model = product.model || "";
      this.variant = product.variant || "";
      this.product_type = product.product_type?.id;

      let locale = product.locale?.[0];
      this.locale = (product.locale || []).map(
        ({ languages_code, language, prices, attributes, ...other_props }) => ({
          ...other_props,
          language: languages_code || language,
          prices: prices || [],
          attributes: attributes || []
        })
      );

      var short_description = locale?.short_description || "";

      // Replace newlines with <br/> tags for non HTML content
      if (!short_description.match(/<[^>]*>/)) {
        if (short_description.length > 0) {
          // Sanitize short description: trim leading/trailing whitespace
          const list_items = short_description
            .trim()
            // Split by newlines
            .split("\n")
            // Limit to 3 lines
            .slice(0, 3);

          short_description = list_items;
        } else {
          short_description = [];
        }
      }

      this.short_description = short_description;

      this.linked_comparison = locale?.linked_comparison?.id;

      this.product_image = locale?.product_image;

      this.image_url = getImageUrl(locale?.product_image?.id);
      this.image_alt = locale?.product_image?.title;

      this.spec_sheet_url = locale?.spec_sheet_url;
      this.product_info_link_url = locale?.product_info_link_url;
      this.product_info_link_label = locale?.product_info_link_label;

      this.description = locale?.description;
      this.description_disclaimer = locale?.description_disclaimer;

      this.tco_black = locale?.tco_black;
      this.tco_color = locale?.tco_color;
    }

    map(collections, maps) {
      this.family = maps.families[this.family]?.family || "";
      this.brand = maps.brands[this.brand];
      this.product_type = maps.product_types[this.product_type];
      this.linked_comparison = maps.comparison_pages?.[this.linked_comparison];

      const getImageThumbnail = (asset_id) => {
        if (!asset_id) {
          return "";
        }
        return `${asset_id}?key=thumbnail`;
      };

      // this.image_thumbnail_url = `${base_url}assets/${this.image_hash}?key=thumbnail`;
      // this.image_product_compare_thumbnail_url = `${base_url}assets/${this.image_hash}?key=product_compare_thumbnail`;

      this.description_disclaimer =
        maps.disclaimers[this.description_disclaimer];

      this.family_model_variant = [this.family, this.model, this.variant]
        .join(" ")
        .replace(/ +/g, " ")
        .trim();

      this.image_thumbnail_url = `${getImageUrl(
        this.product_image?.id
      )}?key=thumbnail`;
      this.image_product_compare_thumbnail_url = `${getImageUrl(
        this.product_image?.id
      )}?key=product_compare_thumbnail`;

      // Set linking for index page
      if (this.linked_comparison)
        this.href = `#${this.linked_comparison.html_id}`;
      else {
        let comparison_page = find(
          collections.comparison_pages,
          // (comparison_page) => {
          //   return comparison_page.secondary_products.indexOf(this.id) > -1;
          // }
          (comparison_page) => {
            const primary_matches = comparison_page.primary_product === this.id;
            const any_secondary_matches =
              comparison_page.secondary_products.indexOf(this.id) > -1;
            const product_in_comparison =
              primary_matches || any_secondary_matches;
            return product_in_comparison;
          }
        );
        if (comparison_page) this.href = `#${comparison_page.html_id}`;
      }

      // Sort features by facet
      this.locale = this.locale
        .map(({ language, attributes, prices, ...other_props }) => {
          return localizeProductFeatures(
            collections,
            maps,
            language,
            attributes,
            prices,
            other_props
          );
        })
        // Set locale-specific prices
        .map(
          ({
            prices,
            language,
            product_image,
            image_thumbnail_url,
            ...other_props
          }) => {
            const product_image_url = getImageUrl(product_image?.id);
            return {
              ...other_props,
              language,
              image_thumbnail_url: getImageThumbnail(product_image_url),
              product_image: product_image_url,
              prices: prices.map((price) => {
                return {
                  ...price,
                  localized_value: language.currency.localizePriceAmount(
                    price.amount
                  )
                };
              })
            };
          }
        );

      const { global_settings } = collections;
      const { default_locale } = global_settings;
      // e.g. field_name: header
      //      selected_locale: 'es-CL'
      this.getLocalized = (
        field_name,
        selected_locale,
        default_value = null
      ) => {
        const localized_value = getLocalizedOrDefault(
          this,
          field_name,
          { locale: selected_locale },
          { locale: default_locale?.language || default_locale }
        );
        return localized_value || default_value;
      };
    }
  }
};

export default ProductConfig;
